import { useCallback, useEffect, useRef, useState } from "react";
import type { ISelectOption } from "@/ui-kit";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

export type IOptionsPage = {
  options: ISelectOption[];
  hasMore: boolean;
};

export type IFetchOptions = (
  query: string,
  page: number,
  signal: AbortSignal,
) => Promise<IOptionsPage>;

/**
 * Paginated, searchable options for an async ComboBox.
 * `fetchOptions` must be stable: a new function does not reload the list.
 */
export const useSearchOptions = (fetchOptions: IFetchOptions) => {
  const [options, setOptions] = useState<ISelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Cursor of the shown list. Refs: they drive requests, not rendering.
  // `null` query means nothing is loaded or loading.
  const queryRef = useRef<string | null>(null);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const controllerRef = useRef<AbortController | null>(null);

  const fetchPage = useCallback(
    (query: string, page: number) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      queryRef.current = query;

      const isFirstPage = page === 1;
      if (isFirstPage) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      fetchOptions(query, page, controller.signal)
        .then((result) => {
          pageRef.current = page;
          hasMoreRef.current = result.hasMore;
          setOptions((prev) =>
            isFirstPage ? result.options : [...prev, ...result.options],
          );
        })
        .catch(serverErrorHandler())
        .finally(() => {
          // An aborted request must not touch the state of the newer one.
          if (controllerRef.current !== controller) {
            return;
          }
          controllerRef.current = null;
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [fetchOptions],
  );

  const search = useCallback(
    (query: string) => {
      // The list for this query is already shown or loading.
      if (query === queryRef.current) {
        return;
      }
      fetchPage(query, 1);
    },
    [fetchPage],
  );

  const loadMore = useCallback(() => {
    if (
      !hasMoreRef.current ||
      controllerRef.current ||
      queryRef.current === null
    ) {
      return;
    }
    fetchPage(queryRef.current, pageRef.current + 1);
  }, [fetchPage]);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
      // Lets a remount (StrictMode) load the list again.
      queryRef.current = null;
    };
  }, []);

  return { options, loading, loadingMore, search, loadMore };
};
